import React from 'react';
import { UseFormClearErrors } from 'react-hook-form';
import { CustomFieldContract } from '../../../types/CustomField';
import ErrorLabel from '../ErrorLabel';
import OuterLabel from '../OuterLabel';
import Dropdown from '../Dropdown';
import Textbox from '../Textbox';
import Checkbox from '../Checkbox';
import { InputProps } from '../../../types/common';
import HtmlLabel from '../HtmlLabel';

export const CustomFieldDropdownTestId = 'CustomFieldDropdown-input';
export const CustomFieldTextboxTestId = 'CustomFieldTextbox-input';
export const CustomFieldCheckboxTestId = 'CustomFieldCheckbox-input';
export const SubTextCustomFieldDropdownTestId =
  'SubText-CustomFieldDropdown-input';
export const SubTextCustomFieldTextboxTestId =
  'SubText-CustomFieldTextbox-input';
export const SubTextCustomFieldCheckboxTestId =
  'SubText-CustomFieldCheckbox-input';

export type CustomFieldInputProps = InputProps<
  HTMLSelectElement | HTMLInputElement
> & {
  customField: CustomFieldContract;
  customType: string;
  errorLabel?: string;
  name: string;
  clearErrors?: UseFormClearErrors<{
    customFields: { [key: string]: unknown };
  }>;
};

const CustomFieldInput: React.FC<CustomFieldInputProps> = ({
  customType,
  customField,
  errorLabel = undefined,
  register,
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
  name,
  clearErrors,
}: CustomFieldInputProps) => {
  switch (customType) {
    case 'TEXTBOX':
      return (
        <div
          className="flex flex-col space-y-1 m-2"
          data-testid={CustomFieldTextboxTestId}
        >
          <OuterLabel
            inputName={name}
            hasErrors={!!errorLabel}
            renderChild
            className="ml-1"
          >
            {customField.question}
          </OuterLabel>
          <Textbox
            name={name}
            hasErrors={!!errorLabel}
            register={register}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            onChange={onChange}
            autoComplete="off"
            className="custom-field-input checkout-panel-form-input"
            defaultValue={customField.value}
          />
          <ErrorLabel>{errorLabel}</ErrorLabel>
          {customField.questionSubText && (
            <HtmlLabel
              className="text-xs ml-1"
              testId={SubTextCustomFieldTextboxTestId}
            >
              {customField.questionSubText}
            </HtmlLabel>
          )}
        </div>
      );
    case 'CHECKBOX':
      return (
        <div
          className="flex flex-col space-y-1 m-2"
          data-testid={CustomFieldCheckboxTestId}
        >
          <div className="flex flex-row">
            <Checkbox
              name={name}
              hasErrors={!!errorLabel}
              register={register}
              autoComplete="off"
              className="custom-field-checkbox"
              onBlur={onBlur}
              onFocus={onFocus}
              onChange={onChange}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              clearErrors={clearErrors as any}
            />
            <OuterLabel
              inputName={name}
              hasErrors={!!errorLabel}
              renderChild
              className="ml-2"
            >
              {customField.question}
            </OuterLabel>
          </div>
          <ErrorLabel className="ml-8">{errorLabel}</ErrorLabel>
          {customField.questionSubText && (
            <HtmlLabel
              className="text-xs ml-8"
              testId={SubTextCustomFieldCheckboxTestId}
            >
              {customField.questionSubText}
            </HtmlLabel>
          )}
        </div>
      );
    case 'DROPDOWN':
      return (
        <div
          className="flex flex-col space-y-1 m-2"
          data-testid={CustomFieldDropdownTestId}
        >
          <OuterLabel
            inputName={name}
            hasErrors={!!errorLabel}
            renderChild
            className="ml-1"
          >
            {customField.question}
          </OuterLabel>
          <Dropdown
            name={name}
            displayText={customField.question}
            hasErrors={!!errorLabel}
            register={register}
            autoComplete="off"
            className="custom-field-input checkout-panel-form-input"
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            onChange={onChange}
            defaultValue={customField.value}
            options={customField.dropdownValues.map(({ title, value }) => {
              return { label: title, value };
            })}
          />
          <ErrorLabel>{errorLabel}</ErrorLabel>
          {customField.questionSubText && (
            <HtmlLabel
              className="text-xs ml-1"
              testId={SubTextCustomFieldDropdownTestId}
            >
              {customField.questionSubText}
            </HtmlLabel>
          )}
        </div>
      );
    default:
      return null;
  }
};

export default CustomFieldInput;
